import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Check,
    ContentCopy,
    Edit,
    FlagOutlined,
    PushPin,
    VisibilityOutlined,
} from "@mui/icons-material";
import { Product } from "../../../types";
import { DeliveryArea } from "../../../services/deliveryAreaService";
import { FaqEntry } from "../../../services/answerDeskService";
import {
    COPY_SHORTCUT,
    fillAnswer,
    IMAGE_COPY_SHORTCUT,
    needsProduct,
    scopeOf,
    Segment,
    timeAgo,
    topicLabel,
} from "../answerDeskUtils";
import { AnswerImages, AnswerImagesHandle } from "./AnswerImages";

// Long answers start folded to about six lines so one answer can't take
// over the list; "See more" opens the rest.
const COLLAPSED_HEIGHT_PX = 160;

const Collapsible: React.FC<{ children: React.ReactNode; contentKey: string }> = ({ children, contentKey }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [tooLong, setTooLong] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Only fold when it saves real space, not to hide a line or two.
        const measure = () => setTooLong(el.scrollHeight > COLLAPSED_HEIGHT_PX + 40);
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [contentKey]);

    const folded = tooLong && !expanded;
    return (
        <Box>
            <Box
                ref={ref}
                sx={{ position: "relative", maxHeight: folded ? COLLAPSED_HEIGHT_PX : "none", overflow: "hidden" }}
            >
                {children}
                {folded && (
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 56,
                            background: (t) => `linear-gradient(rgba(255,255,255,0), ${t.palette.background.paper})`,
                        }}
                    />
                )}
            </Box>
            {tooLong && (
                <Button
                    size="small"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    sx={{ mt: 0.5, px: 0.5, minWidth: 0 }}
                >
                    {expanded ? "See less" : "See more"}
                </Button>
            )}
        </Box>
    );
};

const SCOPE_SX: Record<string, object> = {
    product: { bgcolor: "#DCE9E6", color: "#12302F" },
    brand_category: { bgcolor: "background.paper", color: "primary.main", border: "1px solid #9DB5B1" },
    category: { bgcolor: "background.paper", color: "primary.main", border: "1px solid #9DB5B1" },
    brand: { bgcolor: "background.paper", color: "primary.main", border: "1px solid #9DB5B1" },
    general: { bgcolor: "action.hover", color: "text.secondary" },
    none: { bgcolor: "warning.light", color: "warning.contrastText" },
};

export const AnswerText: React.FC<{ segments: Segment[]; dense?: boolean }> = ({ segments, dense }) => (
    <Typography
        component="div"
        sx={{
            whiteSpace: "pre-line",
            fontSize: dense ? 14 : 15,
            lineHeight: 1.65,
            wordBreak: "break-word",
        }}
    >
        {segments.map((s, i) =>
            s.live ? (
                <Box key={i} component="span" sx={{ bgcolor: "#E3EEEC", color: "#12302F", borderRadius: 0.5, px: 0.4 }}>
                    {s.text}
                </Box>
            ) : s.missing ? (
                <Box
                    key={i}
                    component="span"
                    title="Filled in when a product is in the chat"
                    sx={{
                        border: "1px dashed",
                        borderColor: "text.disabled",
                        color: "text.secondary",
                        borderRadius: 0.5,
                        px: 0.5,
                        fontSize: "0.85em",
                    }}
                >
                    {s.text.replace(/[{}]/g, "").replace(/_/g, " ")}
                </Box>
            ) : (
                <React.Fragment key={i}>{s.text}</React.Fragment>
            ),
        )}
    </Typography>
);

interface AnswerCardProps {
    entry: FaqEntry;
    /** Products in the chat this answer can be filled with. */
    fillWith: Product[];
    deliveryAreas: DeliveryArea[];
    focused?: boolean;
    copied?: boolean;
    canEdit?: boolean;
    onCopy: (entry: FaqEntry, product: Product | null) => void;
    onFlag: (entry: FaqEntry) => void;
    onEdit?: (entry: FaqEntry) => void;
    onFocus?: () => void;
    /** The images' copy handle, for the image keyboard shortcuts. */
    imagesRef?: React.Ref<AnswerImagesHandle>;
}

export const AnswerCard = forwardRef<HTMLDivElement, AnswerCardProps>(function AnswerCard(
    { entry, fillWith, deliveryAreas, focused, copied, canEdit, onCopy, onFlag, onEdit, onFocus, imagesRef },
    ref,
) {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const scope = scopeOf(entry);
    const answer = entry.answer ?? "";
    const wantsProduct = needsProduct(answer);
    const previewProduct = wantsProduct ? fillWith[0] ?? null : null;
    const segments = fillAnswer(answer, previewProduct, deliveryAreas);
    const blocked = wantsProduct && fillWith.length === 0;

    const handleCopyClick = (e: React.MouseEvent<HTMLElement>) => {
        if (wantsProduct && fillWith.length > 1) {
            setMenuAnchor(e.currentTarget);
            return;
        }
        onCopy(entry, previewProduct);
    };

    return (
        <Paper
            ref={ref}
            variant="outlined"
            onFocus={onFocus}
            sx={{
                p: { xs: 1.75, sm: 2 },
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                borderWidth: focused ? 2 : 1,
                borderColor: focused ? "primary.main" : "divider",
                m: focused ? 0 : "1px",
                scrollMarginTop: 96,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={scope.label} sx={{ fontWeight: 500, maxWidth: "100%", ...SCOPE_SX[scope.kind] }} />
                <Typography variant="caption" color="text.secondary">
                    {topicLabel(entry.topic)}
                </Typography>
                {entry.is_pinned && (
                    <Tooltip title="Pinned">
                        <PushPin sx={{ fontSize: 16, color: "text.secondary" }} />
                    </Tooltip>
                )}
            </Stack>

            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                {entry.question}
            </Typography>

            <Collapsible contentKey={segments.map((seg) => seg.text).join("")}>
                <AnswerText segments={segments} />
            </Collapsible>

            {entry.images?.length > 0 && (
                <AnswerImages ref={imagesRef} images={entry.images} question={entry.question} shortcutHints={focused} />
            )}

            {blocked && (
                <Typography variant="caption" color="text.secondary">
                    Add the product to the chat to fill in its price, stock and link.
                </Typography>
            )}
            {wantsProduct && fillWith.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                    Shown for {fillWith[0].name}. Copy lets you pick the product.
                </Typography>
            )}

            {entry.internal_note && (
                <Alert
                    icon={<VisibilityOutlined fontSize="small" />}
                    severity="warning"
                    sx={{ py: 0.25, "& .MuiAlert-message": { fontSize: 13 } }}
                >
                    <strong>For you, not the customer:</strong> {entry.internal_note}
                </Alert>
            )}

            <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: "1 1 auto" }}>
                    {entry.flagged_at
                        ? "Flagged as outdated — a manager will check it"
                        : `Reviewed ${timeAgo(entry.last_reviewed_at)} · copied ${entry.copy_count} ${entry.copy_count === 1 ? "time" : "times"}`}
                </Typography>
                {canEdit && onEdit && (
                    <Button size="small" color="inherit" startIcon={<Edit fontSize="small" />} onClick={() => onEdit(entry)}>
                        Edit
                    </Button>
                )}
                <Button
                    size="small"
                    color="inherit"
                    startIcon={<FlagOutlined fontSize="small" />}
                    onClick={() => onFlag(entry)}
                    disabled={!!entry.flagged_at}
                    sx={{ color: "text.secondary" }}
                >
                    {entry.flagged_at ? "Flagged" : "Flag outdated"}
                </Button>
                {focused && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                        Enter ↵ or {COPY_SHORTCUT}
                        {entry.images?.length > 0 && ` · ${IMAGE_COPY_SHORTCUT} image`}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    disableElevation
                    color={copied ? "success" : "primary"}
                    startIcon={copied ? <Check /> : <ContentCopy fontSize="small" />}
                    onClick={handleCopyClick}
                    disabled={blocked}
                    sx={{ minWidth: 116, minHeight: 40, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                >
                    {copied ? "Copied" : "Copy"}
                </Button>
            </Stack>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem disabled sx={{ fontSize: 13 }}>
                    Copy for which product?
                </MenuItem>
                {fillWith.map((p) => (
                    <MenuItem
                        key={p.product_id}
                        onClick={() => {
                            setMenuAnchor(null);
                            onCopy(entry, p);
                        }}
                    >
                        {p.name}
                    </MenuItem>
                ))}
            </Menu>
        </Paper>
    );
});
